import {useState} from 'react';
import {useNavigate} from 'react-router-dom';

import Cookies from 'js-cookie';
import {makePatchFinishRequestPreview} from 'main/factories/usecases/private/refund/request-preview';
import {FeaturesEnum, HomeEnum, OperationStateTypesEnum, RefundEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {translations} from 'presentation/translations-new/';
import {useRequestPreviewDataStore} from '../../pages/RequestPreview/store/useRequestPreviewDataStore';
import {useRequestPreviewFlowStore} from '../../pages/RequestPreview/store/useRequestPreviewFlowStore';
import {useStepPreviewStore} from '../../pages/RequestPreview/store/useStepPreviewStore';
import {StepPreviewEnum, StepPreviewEnumV2} from '../../pages/RequestPreview/types';
import {TITLE_BY_STEP, TITLE_BY_STEP_V2} from './constants';

const {steps} = translations['pt-br'].privateFlow.previewRefund;

export const useRequestPreview = () => {
	const navigate = useNavigate();
	const {isEnabled: isRefundPreviewCalcEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_REFUND_PREVIEW_CALC,
	);
	const step = useStepPreviewStore(state => state.step);
	const setStep = useStepPreviewStore(state => state.setStep);
	const nextStep = useStepPreviewStore(state => state.nextStep);
	const previousStep = useStepPreviewStore(state => state.previousStep);
	const resetSteps = useStepPreviewStore(state => state.resetStep);
	const useServiceTypeFormV2 = useRequestPreviewFlowStore(
		state => state.useServiceTypeFormV2,
	);
	const resetFlow = useRequestPreviewFlowStore(state => state.resetFlow);
	const setUseServiceTypeFormV2 = useRequestPreviewFlowStore(
		state => state.setUseServiceTypeFormV2,
	);
	const isPdfDownloading = useRequestPreviewFlowStore(state => state.isPdfDownloading);
	const setIsPdfExitWarningOpen = useRequestPreviewFlowStore(
		state => state.setIsPdfExitWarningOpen,
	);
	const [exitFlowModalState, setExitFlowModalState] = useState(false);
	const requestPreviewData = useRequestPreviewDataStore(state => state.data);
	const [conclusionModalState, setConclusionModalState] = useState<boolean>(false);
	const [concludeRequestPreviewState, setConcludeRequestPreviewState] = useState(
		OperationStateTypesEnum.NONE,
	);

	const title = useServiceTypeFormV2 ? TITLE_BY_STEP_V2[step] : TITLE_BY_STEP[step];

	const stepsLabels = [steps.beneficiaryData, steps.service, steps.uploadDocuments, steps.review];
	const stepLabelsV2 = [steps.beneficiaryData, steps.serviceV2, steps.procedure];

	const stepIndexForStepper =
		useServiceTypeFormV2 && step === StepPreviewEnumV2.RESULT
			? stepLabelsV2.length
			: step;

	const IS_LOADING = concludeRequestPreviewState === OperationStateTypesEnum.SUBMITTING;
	const IS_ERROR = concludeRequestPreviewState === OperationStateTypesEnum.ERROR;

	const handleGoToNextStep = async () => {
		if (
			!useServiceTypeFormV2 &&
			step === StepPreviewEnum.REVIEW
		) {
			if (IS_LOADING) return;
			try {
				setConcludeRequestPreviewState(OperationStateTypesEnum.SUBMITTING);
				await makePatchFinishRequestPreview(requestPreviewData.protocolCode).patch();
				setConcludeRequestPreviewState(OperationStateTypesEnum.DONE);
				setConclusionModalState(true);
				Cookies.remove('uploadFilesTerm');
				return;
			} catch (error) {
				setConcludeRequestPreviewState(OperationStateTypesEnum.ERROR);
				return;
			}
		}

		nextStep();
	};

	const hasBeneficiaryOrFilledData = () =>
		Boolean(requestPreviewData.beneficiary?.userCode) ||
		(requestPreviewData.files?.length ?? 0) > 0;

	const handleBack = () => {
		if (step === StepPreviewEnum.BENEFICIARY_DATA || step === StepPreviewEnumV2.BENEFICIARY_DATA) {
			if (useServiceTypeFormV2) {
				if (hasBeneficiaryOrFilledData()) {
					setExitFlowModalState(true);
					return;
				}
				resetSteps();
				resetFlow();
				navigate(RefundEnum.REFUND);
				return;
			}
			setExitFlowModalState(true);
			return;
		}
		if (step === StepPreviewEnum.SERVICE_TYPE && !useServiceTypeFormV2 && isRefundPreviewCalcEnabled) {
			setUseServiceTypeFormV2(true);
			setStep(StepPreviewEnumV2.SERVICE_TYPE);
			return;
		}
		if (useServiceTypeFormV2 && hasBeneficiaryOrFilledData()) {
			setExitFlowModalState(true);
			return;
		}
		previousStep();
	};

	const handleClose = () => {
		const isLastStep =
			step === StepPreviewEnum.REVIEW || step === StepPreviewEnumV2.RESULT;
		if (isLastStep) {
			if (isPdfDownloading) {
				setIsPdfExitWarningOpen(true);
				return;
			}
			resetSteps();
			resetFlow();
			navigate(RefundEnum.REFUND, {replace: true});
			Cookies.remove('uploadFilesTerm');
		} else {
			setExitFlowModalState(true);
			Cookies.remove('uploadFilesTerm');
		}
	};

	const handleAbandonFlowModalAction = () => {
		resetSteps();
		resetFlow();
		navigate(RefundEnum.REFUND);
	};

	const handleBackToRefundMenu = () => {
		resetSteps();
		resetFlow();
		navigate(RefundEnum.REFUND, {replace: true});
	};

	const handleGoToHome = () => {
		navigate(HomeEnum.HOME_PAGE);
	};

	return {
		handleClose,
		handleAbandonFlowModalAction,
		handleBackToRefundMenu,
		handleGoToHome,
		handleBack,
		handleGoToNextStep,
		IS_ERROR,
		IS_LOADING,
		stepsLabels: useServiceTypeFormV2 ? stepLabelsV2 : stepsLabels,
		stepIndexForStepper,
		title,
		exitFlowModalState,
		conclusionModalState,
		step,
		setExitFlowModalState,
		setConclusionModalState,
		setConcludeRequestPreviewState,
		useServiceTypeFormV2,
	};
};
